// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import {
  type BffInboundMessage,
  type FriendListSnapshotEvent,
  type FriendSnapshot,
  getSharedBffWebSocket,
  type PlayerTemporaryCreatedEvent,
} from '@/services/bffWebSocket';

type PlatformPlayer = {
  id: string;
  name: string;
  userId?: string | null;
};

type FriendFilter = 'all' | 'recent' | 'elite';
type ExpandedAddPanel = 'linked' | 'guest' | null;

export default function FriendsScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [friends, setFriends] = useState<FriendSnapshot[]>([]);
  const [platformPlayers, setPlatformPlayers] = useState<PlatformPlayer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [guestName, setGuestName] = useState('');
  const [activeFilter] = useState<FriendFilter>('all');
  const [expandedAddPanel, setExpandedAddPanel] = useState<ExpandedAddPanel>('linked');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [nicknameModalFriendId, setNicknameModalFriendId] = useState<string | null>(null);
  const [nicknameModalValue, setNicknameModalValue] = useState('');
  const friendsRef = useRef<FriendSnapshot[]>([]);
  const guestNameRef = useRef('');
  const pendingGuestRequestIdRef = useRef<string | null>(null);
  const pendingFriendAddRequestIdRef = useRef<string | null>(null);
  const addRollbackFriendsRef = useRef<FriendSnapshot[] | null>(null);
  const pendingRemoveFriendIdRef = useRef<string | null>(null);
  const removeRollbackFriendsRef = useRef<FriendSnapshot[] | null>(null);
  const socket = useMemo(() => getSharedBffWebSocket(apiBaseUrl), [apiBaseUrl]);

  const requestSnapshot = useCallback(() => {
    if (!user?.token) {
      return;
    }
    setLoading(true);
    socket.setAuthToken(user.token);
    socket.sendFriendListRequest();
  }, [socket, user?.token]);

  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);

  useEffect(() => {
    guestNameRef.current = guestName;
  }, [guestName]);

  useEffect(() => {
    if (!user?.token) {
      return;
    }

    let cancelled = false;
    const loadPlatformPlayers = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/players/platform`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`players.platform ${response.status}`);
        }
        const payload = (await response.json()) as Array<Record<string, unknown>>;
        if (cancelled) {
          return;
        }
        setPlatformPlayers(
          (payload ?? [])
            .map((entry) => ({
              id: ((entry.id as string | undefined) ?? (entry.playerId as string | undefined) ?? '').trim(),
              name: ((entry.name as string | undefined) ?? '').trim(),
              userId: (entry.userId as string | null | undefined) ?? null,
            }))
            .filter((entry) => entry.id.length > 0 && entry.name.length > 0)
        );
      } catch {
        if (!cancelled) {
          setError(t('friends.loadPlayersError'));
        }
      }
    };

    void loadPlatformPlayers();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, t, user?.token]);

  useEffect(() => {
    if (!user?.token) {
      return;
    }

    socket.setAuthToken(user.token);
    requestSnapshot();

    const unsubscribe = socket.onMessage((message: BffInboundMessage) => {
      const type = (message as { type?: string }).type;

      if (type === 'friend.list.snapshot') {
        const payload = message as FriendListSnapshotEvent;
        setFriends(payload.friends ?? []);
        setError(null);
        setLoading(false);
        setSaving(false);
        return;
      }

      if (type === 'friend.removed') {
        const payload = message as { friendId?: string };
        if (payload.friendId) {
          setFriends((current) => current.filter((friend) => friend.friendId !== payload.friendId));
        }
        if (!payload.friendId || payload.friendId === pendingRemoveFriendIdRef.current) {
          pendingRemoveFriendIdRef.current = null;
          removeRollbackFriendsRef.current = null;
          setSaving(false);
        }
        requestSnapshot();
        return;
      }

      if (type === 'friend.added' || type === 'friend.updated') {
        if (type === 'friend.added') {
          pendingFriendAddRequestIdRef.current = null;
          addRollbackFriendsRef.current = null;
        }
        requestSnapshot();
        setSaving(false);
        return;
      }

      if (type === 'friend.add.rejected') {
        if (addRollbackFriendsRef.current) {
          setFriends(addRollbackFriendsRef.current);
        }
        pendingFriendAddRequestIdRef.current = null;
        addRollbackFriendsRef.current = null;
        setSaving(false);
        setLoading(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('friends.actionFailed'));
        return;
      }

      if (type === 'friend.remove.rejected') {
        if (removeRollbackFriendsRef.current) {
          setFriends(removeRollbackFriendsRef.current);
        }
        pendingRemoveFriendIdRef.current = null;
        removeRollbackFriendsRef.current = null;
        setSaving(false);
        setLoading(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('friends.actionFailed'));
        return;
      }

      if (type === 'friend.update.rejected') {
        setSaving(false);
        setLoading(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('friends.actionFailed'));
        return;
      }

      if (type === 'player.temporary.created') {
        const payload = message as PlayerTemporaryCreatedEvent;
        if (!payload.requestId || payload.requestId !== pendingGuestRequestIdRef.current) {
          return;
        }
        pendingGuestRequestIdRef.current = null;

        if (!payload.playerId) {
          setSaving(false);
          setError(t('friends.guestCreateFailed'));
          return;
        }

        const friendAddRequestId = generateRequestId();
        const guestDisplayName = (payload.name ?? guestNameRef.current).trim();
        addRollbackFriendsRef.current = friendsRef.current;
        pendingFriendAddRequestIdRef.current = friendAddRequestId;
        setFriends((current) => {
          if (current.some((friend) => friend.playerId === payload.playerId)) {
            return current;
          }
          return [
            ...current,
            {
              friendId: `pending-${friendAddRequestId}`,
              playerId: payload.playerId,
              nickname: null,
              name: guestDisplayName || t('friends.guest'),
              playerName: guestDisplayName || null,
              userId: null,
              status: 'guest',
            },
          ];
        });
        const sent = socket.sendFriendAdd({
          playerId: payload.playerId,
          nickname: undefined,
          requestId: friendAddRequestId,
        });
        if (!sent) {
          if (addRollbackFriendsRef.current) {
            setFriends(addRollbackFriendsRef.current);
          }
          pendingFriendAddRequestIdRef.current = null;
          addRollbackFriendsRef.current = null;
          setSaving(false);
          setError(t('friends.actionFailed'));
          return;
        }
        setGuestName('');
      }
    });

    return () => {
      unsubscribe();
    };
  }, [requestSnapshot, socket, t, user?.token]);

  const friendPlayerIdSet = useMemo(() => new Set(friends.map((friend) => friend.playerId)), [friends]);

  const linkedCandidates = useMemo(() => {
    const normalized = searchQuery.trim().toLowerCase();
    return platformPlayers
      .filter((player) => player.userId && player.userId !== user?.id && !friendPlayerIdSet.has(player.id))
      .filter((player) => {
        if (!normalized) {
          return true;
        }
        return player.name.toLowerCase().includes(normalized);
      })
      .slice(0, 20);
  }, [friendPlayerIdSet, platformPlayers, searchQuery, user?.id]);

  const visibleFriends = useMemo(() => {
    const normalized = globalSearchQuery.trim().toLowerCase();
    if (!normalized) {
      return friends;
    }
    return friends.filter((friend) => {
      const name = (friend.name ?? '').toLowerCase();
      const nickname = (friend.nickname ?? '').toLowerCase();
      return name.includes(normalized) || nickname.includes(normalized);
    });
  }, [friends, globalSearchQuery]);

  const addFriend = useCallback(
    (playerId: string, nickname?: string) => {
      if (!user?.token || !playerId) {
        return;
      }
      const requestId = generateRequestId();
      const linkedPlayer = platformPlayers.find((player) => player.id === playerId);
      const displayName = (nickname ?? '').trim() || linkedPlayer?.name?.trim() || '';
      addRollbackFriendsRef.current = friends;
      pendingFriendAddRequestIdRef.current = requestId;
      setFriends((current) => {
        if (current.some((friend) => friend.playerId === playerId)) {
          return current;
        }
        return [
          ...current,
          {
            friendId: `pending-${requestId}`,
            playerId,
            nickname: nickname?.trim() || null,
            name: displayName || t('friends.linked'),
            playerName: linkedPlayer?.name ?? null,
            userId: linkedPlayer?.userId ?? null,
            status: 'linked',
          },
        ];
      });
      setSaving(true);
      setError(null);
      socket.setAuthToken(user.token);
      const sent = socket.sendFriendAdd({ playerId, nickname, requestId });
      if (!sent) {
        if (addRollbackFriendsRef.current) {
          setFriends(addRollbackFriendsRef.current);
        }
        pendingFriendAddRequestIdRef.current = null;
        addRollbackFriendsRef.current = null;
        setSaving(false);
        setError(t('friends.actionFailed'));
      }
    },
    [friends, platformPlayers, socket, t, user?.token]
  );

  const addGuestFriend = useCallback(() => {
    if (!user?.token) {
      return;
    }
    const name = guestName.trim();
    if (!name) {
      return;
    }
    setSaving(true);
    setError(null);
    const requestId = generateRequestId();
    pendingGuestRequestIdRef.current = requestId;
    socket.setAuthToken(user.token);
    const sent = socket.send({
      type: 'player.temporary.create',
      requestId,
      name,
    });
    if (!sent) {
      pendingGuestRequestIdRef.current = null;
      setSaving(false);
      setError(t('friends.guestCreateFailed'));
    }
  }, [guestName, socket, t, user?.token]);

  const removeFriend = useCallback(
    (friendId: string) => {
      if (!user?.token || !friendId) {
        return;
      }
      if (friendId.startsWith('pending-')) {
        setFriends((current) => current.filter((friend) => friend.friendId !== friendId));
        setSaving(false);
        return;
      }
      removeRollbackFriendsRef.current = friends;
      pendingRemoveFriendIdRef.current = friendId;
      setFriends((current) => current.filter((friend) => friend.friendId !== friendId));
      setSaving(true);
      setError(null);
      socket.setAuthToken(user.token);
      const sent = socket.sendFriendRemove({ friendId });
      if (!sent) {
        if (removeRollbackFriendsRef.current) {
          setFriends(removeRollbackFriendsRef.current);
        }
        pendingRemoveFriendIdRef.current = null;
        removeRollbackFriendsRef.current = null;
        setSaving(false);
        setError(t('friends.actionFailed'));
      }
    },
    [friends, socket, t, user?.token]
  );

  const updateNickname = useCallback(
    (friendId: string, nickname: string) => {
      if (!user?.token || !friendId) {
        return;
      }
      setSaving(true);
      setError(null);
      socket.setAuthToken(user.token);
      const sent = socket.sendFriendUpdate({ friendId, nickname: nickname.trim() || undefined });
      if (!sent) {
        setSaving(false);
        setError(t('friends.actionFailed'));
      }
    },
    [socket, t, user?.token]
  );

  const openNicknameModal = useCallback((friend: FriendSnapshot) => {
    setNicknameModalFriendId(friend.friendId);
    setNicknameModalValue(friend.nickname ?? '');
  }, []);

  const closeNicknameModal = useCallback(() => {
    setNicknameModalFriendId(null);
    setNicknameModalValue('');
  }, []);

  const saveNicknameFromModal = useCallback(() => {
    if (!nicknameModalFriendId) {
      return;
    }
    updateNickname(nicknameModalFriendId, nicknameModalValue);
    closeNicknameModal();
  }, [closeNicknameModal, nicknameModalFriendId, nicknameModalValue, updateNickname]);

  const goBack = useCallback(() => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/tournaments');
  }, [router]);

  const toggleAddPanel = useCallback((panel: ExpandedAddPanel) => {
    setExpandedAddPanel((current) => (current === panel ? null : panel));
  }, []);

  return (
    <>
      <ScrollView style={styles.screen} contentContainerStyle={styles.container}>
        <Pressable style={styles.backButton} onPress={goBack}>
          <Text style={styles.backButtonText}>{t('menu.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('friends.title')}</Text>
        {loading ? <Text style={styles.note}>{t('friends.loading')}</Text> : null}
        {error ? <Text style={styles.error}>{error}</Text> : null}

        <TextInput
          value={globalSearchQuery}
          onChangeText={setGlobalSearchQuery}
          placeholder={t('friends.findPlayersPlaceholder')}
          placeholderTextColor="#8aa58f"
          style={styles.globalSearchInput}
        />

        <View style={styles.filterRow}>
          <Pressable style={[styles.filterChip, activeFilter === 'all' ? styles.filterChipActive : null]}>
            <Text style={[styles.filterChipText, activeFilter === 'all' ? styles.filterChipTextActive : null]}>
              {t('friends.allFriends')}
            </Text>
          </Pressable>
          <View style={[styles.filterChip, styles.filterChipDisabled]}>
            <Text style={styles.filterChipText}>{t('friends.recent')}</Text>
          </View>
          <View style={[styles.filterChip, styles.filterChipDisabled]}>
            <Text style={styles.filterChipText}>{t('friends.eliteStatus')}</Text>
          </View>
        </View>

        <View style={styles.cardRow}>
          <Pressable
            style={[styles.addCard, styles.addCardPrimary, expandedAddPanel === 'linked' ? styles.addCardActive : null]}
            onPress={() => toggleAddPanel('linked')}
          >
            <Text style={styles.addCardTitle}>{t('friends.addLinked')}</Text>
          </Pressable>
          <Pressable
            style={[styles.addCard, styles.addCardSecondary, expandedAddPanel === 'guest' ? styles.addCardActive : null]}
            onPress={() => toggleAddPanel('guest')}
          >
            <Text style={[styles.addCardTitle, styles.addCardTitleOnDark]}>{t('friends.addGuest')}</Text>
          </Pressable>
        </View>

        {expandedAddPanel === 'linked' ? (
          <View style={styles.inlinePanel}>
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder={t('friends.searchPlaceholder')}
              placeholderTextColor="#8aa58f"
              style={styles.input}
            />
            {linkedCandidates.map((candidate) => (
              <View key={candidate.id} style={styles.candidateRow}>
                <Text style={styles.name}>{candidate.name}</Text>
                <Pressable style={styles.actionButton} onPress={() => addFriend(candidate.id)}>
                  <Text style={styles.actionText}>{t('friends.add')}</Text>
                </Pressable>
              </View>
            ))}
          </View>
        ) : null}

        {expandedAddPanel === 'guest' ? (
          <View style={styles.inlinePanel}>
            <TextInput
              value={guestName}
              onChangeText={setGuestName}
              placeholder={t('friends.guestPlaceholder')}
              placeholderTextColor="#8aa58f"
              style={styles.input}
            />
            <GoffyButton
              label={t('friends.createGuestAndAdd')}
              onPress={addGuestFriend}
              disabled={guestName.trim().length === 0}
              tone="accent"
            />
          </View>
        ) : null}

        <View style={styles.friendsHeaderRow}>
          <Text style={styles.friendsHeader}>{t('friends.myFriendsCount', { count: friends.length })}</Text>
        </View>

        {friends.length === 0 ? <Text style={styles.note}>{t('friends.empty')}</Text> : null}
        {friends.length > 0 && visibleFriends.length === 0 ? <Text style={styles.note}>{t('friends.noMatches')}</Text> : null}

        {visibleFriends.map((friend) => {
          const normalizedName = (friend.name ?? '').trim().toLowerCase();
          const normalizedNickname = (friend.nickname ?? '').trim();
          const showNickname = normalizedNickname.length > 0 && normalizedNickname.toLowerCase() !== normalizedName;

          return (
            <View key={friend.friendId} style={styles.friendCard}>
            <View style={styles.friendCardLeading}>
              <View style={styles.avatarPlaceholder}>
                <Text style={styles.avatarLetter}>{(friend.name?.trim()?.charAt(0) || '?').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.friendCardContent}>
              <View style={styles.friendCardBody}>
                <View style={styles.friendTitleRow}>
                  <Text style={styles.friendName} numberOfLines={1}>
                    {friend.name}
                  </Text>
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>{friend.status === 'linked' ? t('friends.linked') : t('friends.guest')}</Text>
                  </View>
                </View>
                {showNickname ? (
                  <Text style={styles.friendMeta} numberOfLines={1}>
                    {friend.nickname}
                  </Text>
                ) : null}
              </View>
              <View style={styles.friendActions}>
                <Pressable style={styles.iconButton} onPress={() => openNicknameModal(friend)}>
                  <Text style={styles.iconButtonText}>{t('friends.editNickname')}</Text>
                </Pressable>
                <Pressable style={styles.iconDangerButton} onPress={() => removeFriend(friend.friendId)}>
                  <Text style={styles.iconButtonText}>{t('friends.remove')}</Text>
                </Pressable>
              </View>
            </View>
            </View>
          );
        })}
      </ScrollView>
      <Modal transparent visible={nicknameModalFriendId !== null} animationType="fade" onRequestClose={closeNicknameModal}>
        <Pressable style={styles.modalBackdrop} onPress={closeNicknameModal}>
          <Pressable style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('friends.editNickname')}</Text>
            <TextInput
              value={nicknameModalValue}
              onChangeText={setNicknameModalValue}
              placeholder={t('friends.nicknamePlaceholder')}
              placeholderTextColor="#7b9a82"
              style={styles.input}
              autoFocus
            />
            <View style={styles.modalActions}>
              <Pressable style={styles.actionButton} onPress={saveNicknameFromModal}>
                <Text style={styles.actionText}>{t('friends.saveNickname')}</Text>
              </Pressable>
              <Pressable style={styles.dangerButton} onPress={closeNicknameModal}>
                <Text style={styles.actionText}>{t('friends.cancel')}</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#031907',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 10,
    paddingTop: 8,
    gap: 10,
    backgroundColor: '#031907',
    paddingBottom: 24,
  },
  backButton: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#245932',
    backgroundColor: '#123b1f',
    paddingHorizontal: 12,
    height: 38,
    justifyContent: 'center',
  },
  backButtonText: {
    color: '#d4f5da',
    fontWeight: '700',
    fontSize: 13,
  },
  title: {
    color: '#d4f5da',
    fontSize: 26,
    fontWeight: '800',
  },
  globalSearchInput: {
    borderWidth: 1,
    borderColor: '#2d6025',
    borderRadius: 10,
    color: '#d4f5da',
    backgroundColor: '#0c110b',
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 13,
    letterSpacing: 1,
  },
  filterRow: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#214f2a',
    paddingHorizontal: 14,
    paddingVertical: 7,
    backgroundColor: '#0a220f',
  },
  filterChipActive: {
    borderColor: '#77d23a',
    backgroundColor: '#133316',
  },
  filterChipDisabled: {
    opacity: 0.45,
  },
  filterChipText: {
    color: '#34d69d',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  filterChipTextActive: {
    color: '#b3ff75',
  },
  cardRow: {
    flexDirection: 'row',
    gap: 8,
  },
  addCard: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    minHeight: 86,
    paddingHorizontal: 12,
    paddingVertical: 10,
    justifyContent: 'flex-end',
  },
  addCardPrimary: {
    borderColor: '#84e33f',
    backgroundColor: '#8dde2e',
  },
  addCardSecondary: {
    borderColor: '#1b4e26',
    backgroundColor: '#052713',
  },
  addCardActive: {
    transform: [{ scale: 1.01 }],
    shadowColor: '#8dde2e',
    shadowOpacity: 0.35,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
  },
  addCardTitle: {
    color: '#0d240e',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    lineHeight: 17,
  },
  addCardTitleOnDark: {
    color: '#d4f5da',
  },
  inlinePanel: {
    borderWidth: 1,
    borderColor: '#1e5a2d',
    borderRadius: 12,
    padding: 8,
    backgroundColor: '#062111',
    gap: 6,
  },
  friendsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  friendsHeader: {
    color: '#d6f4da',
    fontSize: 34,
    fontWeight: '900',
    letterSpacing: -1,
  },
  friendCard: {
    borderWidth: 1,
    borderColor: '#0f3c1f',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 8,
    backgroundColor: '#062411',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  friendCardLeading: {
    width: 44,
    alignItems: 'center',
  },
  avatarPlaceholder: {
    width: 38,
    height: 38,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2f6a3d',
    backgroundColor: '#0b3a1b',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    color: '#adf36a',
    fontSize: 14,
    fontWeight: '800',
  },
  friendCardBody: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  friendCardContent: {
    flex: 1,
    gap: 8,
    minWidth: 0,
  },
  friendTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  friendName: {
    color: '#d6f4da',
    fontSize: 17,
    fontWeight: '800',
    flex: 1,
    flexShrink: 1,
  },
  badge: {
    borderWidth: 1,
    borderColor: '#245c30',
    backgroundColor: '#13371b',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  badgeText: {
    color: '#8fcf9c',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  friendMeta: {
    color: '#8fcf9c',
    fontSize: 12,
    fontWeight: '600',
  },
  friendActions: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  iconButton: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: '#1e4d22',
  },
  iconDangerButton: {
    borderWidth: 1,
    borderColor: '#f87171',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: '#7f1d1d',
  },
  iconButtonText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  note: {
    color: '#9bcfa8',
    fontSize: 12,
  },
  error: {
    color: '#fecaca',
    fontSize: 12,
  },
  candidateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  name: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    flex: 1,
  },
  input: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 8,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
    paddingHorizontal: 8,
    paddingVertical: 7,
    fontSize: 12,
  },
  actionButton: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: '#1e4d22',
  },
  dangerButton: {
    borderWidth: 1,
    borderColor: '#f87171',
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: '#7f1d1d',
  },
  actionText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    borderWidth: 1,
    borderColor: '#2a6a36',
    borderRadius: 10,
    backgroundColor: '#0a2814',
    padding: 10,
    gap: 8,
  },
  modalTitle: {
    color: '#d4f5da',
    fontSize: 14,
    fontWeight: '700',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 6,
    justifyContent: 'flex-end',
  },
});
